// Soundscape Service - Manages dynamic audio environment
import { elevenLabsService } from './elevenlabsService';

interface SoundscapeLayer {
  id: string;
  name: string;
  audioUrl: string;
  volume: number;
  loop: boolean;
  fadeInDuration: number;
  fadeOutDuration: number;
  isPlaying: boolean;
  audioElement?: HTMLAudioElement;
}

interface SoundscapeProfile {
  id: string;
  name: string;
  description: string;
  layers: SoundscapeLayer[];
  ambientVolume: number;
  musicVolume: number;
  effectsVolume: number;
}

interface LocationSoundscape {
  location: string;
  timeOfDay: 'dawn' | 'day' | 'dusk' | 'night';
  weather: string;
  mood: 'tense' | 'calm' | 'mysterious' | 'action' | 'melancholy';
  profileId: string;
}

class SoundscapeService {
  private currentProfile: SoundscapeProfile | null = null;
  private activeLayers: Map<string, SoundscapeLayer> = new Map();
  private masterVolume = 0.7;
  private isEnabled = true;
  private fadeIntervals: Map<string, NodeJS.Timeout> = new Map();
  private locationSoundscapes: LocationSoundscape[] = [];

  constructor() {
    this.initializeSoundscapeProfiles();
    this.setupVolumeControls();
  }

  private initializeSoundscapeProfiles(): void {
    // Initialize with cyberpunk soundscape profiles
    this.locationSoundscapes = [
      {
        location: 'neo-tokyo-plaza',
        timeOfDay: 'day',
        weather: 'clear',
        mood: 'calm',
        profileId: 'urban_day'
      },
      {
        location: 'neo-tokyo-plaza',
        timeOfDay: 'night',
        weather: 'rain',
        mood: 'mysterious',
        profileId: 'urban_night_rain'
      },
      {
        location: 'cyberspace',
        timeOfDay: 'day',
        weather: 'digital_storm',
        mood: 'tense',
        profileId: 'digital_realm'
      },
      {
        location: 'underground',
        timeOfDay: 'night',
        weather: 'clear',
        mood: 'mysterious',
        profileId: 'underground_base'
      }
    ];
  }

  private setupVolumeControls(): void {
    // Listen for user preference changes
    const savedVolume = localStorage.getItem('lucidCyberRPG_masterVolume');
    if (savedVolume) {
      this.masterVolume = parseFloat(savedVolume);
    }

    const soundEnabled = localStorage.getItem('lucidCyberRPG_soundEnabled');
    if (soundEnabled !== null) {
      this.isEnabled = soundEnabled === 'true';
    }
  }

  /**
   * Generate dynamic soundscape based on context
   */
  public async generateContextualSoundscape(context: {
    location: string;
    timeOfDay: 'dawn' | 'day' | 'dusk' | 'night';
    weather: string;
    mood: 'tense' | 'calm' | 'mysterious' | 'action' | 'melancholy';
    worldKarma: number;
  }): Promise<void> {
    if (!this.isEnabled) return;

    console.log('[Soundscape] Generating contextual soundscape:', context);

    try {
      // Find matching soundscape profile
      const matchingProfile = this.findMatchingSoundscape(context);
      
      if (matchingProfile) {
        await this.transitionToProfile(matchingProfile);
      } else {
        // Generate new soundscape profile
        const newProfile = await this.createDynamicSoundscape(context);
        if (newProfile) {
          await this.transitionToProfile(newProfile);
        }
      }
    } catch (error) {
      console.error('[Soundscape] Failed to generate contextual soundscape:', error);
    }
  }

  private findMatchingSoundscape(context: {
    location: string;
    timeOfDay: 'dawn' | 'day' | 'dusk' | 'night';
    weather: string;
    mood: 'tense' | 'calm' | 'mysterious' | 'action' | 'melancholy';
  }): SoundscapeProfile | null {
    // Find the best matching soundscape
    const locationMatch = this.locationSoundscapes.find(ls => 
      ls.location === context.location &&
      ls.timeOfDay === context.timeOfDay &&
      ls.mood === context.mood
    );

    if (locationMatch) {
      return this.getSoundscapeProfile(locationMatch.profileId);
    }

    return null;
  }

  private getSoundscapeProfile(profileId: string): SoundscapeProfile | null {
    // Return predefined soundscape profiles
    const profiles: { [key: string]: SoundscapeProfile } = {
      'urban_day': {
        id: 'urban_day',
        name: 'Urban Day',
        description: 'Bustling city sounds during daytime',
        ambientVolume: 0.6,
        musicVolume: 0.4,
        effectsVolume: 0.7,
        layers: [
          {
            id: 'traffic_ambient',
            name: 'Traffic Ambient',
            audioUrl: this.generateAudioUrl('urban_traffic_distant'),
            volume: 0.3,
            loop: true,
            fadeInDuration: 3000,
            fadeOutDuration: 2000,
            isPlaying: false
          },
          {
            id: 'crowd_chatter',
            name: 'Crowd Chatter',
            audioUrl: this.generateAudioUrl('crowd_distant_chatter'),
            volume: 0.2,
            loop: true,
            fadeInDuration: 4000,
            fadeOutDuration: 3000,
            isPlaying: false
          },
          {
            id: 'cyberpunk_music',
            name: 'Cyberpunk Ambient Music',
            audioUrl: this.generateAudioUrl('cyberpunk_ambient_day'),
            volume: 0.4,
            loop: true,
            fadeInDuration: 5000,
            fadeOutDuration: 4000,
            isPlaying: false
          }
        ]
      },
      'urban_night_rain': {
        id: 'urban_night_rain',
        name: 'Urban Night Rain',
        description: 'Rainy night in the cyberpunk city',
        ambientVolume: 0.7,
        musicVolume: 0.5,
        effectsVolume: 0.6,
        layers: [
          {
            id: 'rain_heavy',
            name: 'Heavy Rain',
            audioUrl: this.generateAudioUrl('rain_on_concrete'),
            volume: 0.5,
            loop: true,
            fadeInDuration: 2000,
            fadeOutDuration: 3000,
            isPlaying: false
          },
          {
            id: 'neon_hum',
            name: 'Neon Hum',
            audioUrl: this.generateAudioUrl('neon_electrical_hum'),
            volume: 0.3,
            loop: true,
            fadeInDuration: 4000,
            fadeOutDuration: 2000,
            isPlaying: false
          },
          {
            id: 'night_music',
            name: 'Night Ambient Music',
            audioUrl: this.generateAudioUrl('cyberpunk_ambient_night'),
            volume: 0.4,
            loop: true,
            fadeInDuration: 6000,
            fadeOutDuration: 5000,
            isPlaying: false
          }
        ]
      },
      'digital_realm': {
        id: 'digital_realm',
        name: 'Digital Realm',
        description: 'Sounds of cyberspace and digital environments',
        ambientVolume: 0.8,
        musicVolume: 0.6,
        effectsVolume: 0.5,
        layers: [
          {
            id: 'data_streams',
            name: 'Data Streams',
            audioUrl: this.generateAudioUrl('digital_data_flow'),
            volume: 0.4,
            loop: true,
            fadeInDuration: 3000,
            fadeOutDuration: 2000,
            isPlaying: false
          },
          {
            id: 'server_hum',
            name: 'Server Hum',
            audioUrl: this.generateAudioUrl('server_room_ambient'),
            volume: 0.3,
            loop: true,
            fadeInDuration: 2000,
            fadeOutDuration: 2000,
            isPlaying: false
          },
          {
            id: 'digital_music',
            name: 'Digital Ambient Music',
            audioUrl: this.generateAudioUrl('cyberpunk_digital_ambient'),
            volume: 0.5,
            loop: true,
            fadeInDuration: 4000,
            fadeOutDuration: 4000,
            isPlaying: false
          }
        ]
      },
      'underground_base': {
        id: 'underground_base',
        name: 'Underground Base',
        description: 'Hidden underground facility sounds',
        ambientVolume: 0.5,
        musicVolume: 0.3,
        effectsVolume: 0.6,
        layers: [
          {
            id: 'ventilation',
            name: 'Ventilation System',
            audioUrl: this.generateAudioUrl('ventilation_system'),
            volume: 0.3,
            loop: true,
            fadeInDuration: 2000,
            fadeOutDuration: 2000,
            isPlaying: false
          },
          {
            id: 'machinery_distant',
            name: 'Distant Machinery',
            audioUrl: this.generateAudioUrl('machinery_distant_hum'),
            volume: 0.2,
            loop: true,
            fadeInDuration: 3000,
            fadeOutDuration: 3000,
            isPlaying: false
          },
          {
            id: 'underground_music',
            name: 'Underground Ambient Music',
            audioUrl: this.generateAudioUrl('cyberpunk_underground_ambient'),
            volume: 0.3,
            loop: true,
            fadeInDuration: 5000,
            fadeOutDuration: 4000,
            isPlaying: false
          }
        ]
      }
    };

    return profiles[profileId] || null;
  }

  private generateAudioUrl(soundType: string): string {
    // In a real implementation, these would be actual audio files
    // For now, return placeholder URLs that could be generated by AI
    const baseUrl = 'https://example.com/audio/';
    return `${baseUrl}${soundType}.mp3`;
  }

  private async createDynamicSoundscape(context: {
    location: string;
    timeOfDay: 'dawn' | 'day' | 'dusk' | 'night';
    weather: string;
    mood: 'tense' | 'calm' | 'mysterious' | 'action' | 'melancholy';
    worldKarma: number;
  }): Promise<SoundscapeProfile | null> {
    try {
      console.log('[Soundscape] Creating dynamic soundscape for context:', context);

      // Generate soundscape description for AI
      const soundscapePrompt = `
        Create a cyberpunk soundscape for the following context:
        Location: ${context.location}
        Time: ${context.timeOfDay}
        Weather: ${context.weather}
        Mood: ${context.mood}
        World Karma: ${context.worldKarma}
        
        Describe 3-4 audio layers that would create an immersive soundscape.
        Include ambient sounds, music, and environmental effects.
        Consider the cyberpunk setting with technology, urban environments, and futuristic elements.
        
        Respond with JSON:
        {
          "name": "soundscape name",
          "description": "brief description",
          "layers": [
            {"name": "layer name", "description": "what this layer sounds like", "volume": 0.1-0.8, "type": "ambient|music|effect"}
          ]
        }
      `;

      // Note: In a real implementation, you might use AI to generate actual audio
      // For now, we'll create a basic profile based on context
      const profile: SoundscapeProfile = {
        id: `dynamic_${Date.now()}`,
        name: `${context.location} - ${context.timeOfDay} - ${context.mood}`,
        description: `Dynamic soundscape for ${context.location}`,
        ambientVolume: 0.6,
        musicVolume: 0.4,
        effectsVolume: 0.5,
        layers: this.generateContextualLayers(context)
      };

      return profile;
    } catch (error) {
      console.error('[Soundscape] Failed to create dynamic soundscape:', error);
      return null;
    }
  }

  private generateContextualLayers(context: {
    location: string;
    timeOfDay: 'dawn' | 'day' | 'dusk' | 'night';
    weather: string;
    mood: 'tense' | 'calm' | 'mysterious' | 'action' | 'melancholy';
    worldKarma: number;
  }): SoundscapeLayer[] {
    const layers: SoundscapeLayer[] = [];

    // Base ambient layer
    layers.push({
      id: `ambient_${Date.now()}`,
      name: 'Ambient Base',
      audioUrl: this.generateAudioUrl(`${context.location}_${context.timeOfDay}_ambient`),
      volume: 0.4,
      loop: true,
      fadeInDuration: 3000,
      fadeOutDuration: 2000,
      isPlaying: false
    });

    // Weather layer
    if (context.weather !== 'clear') {
      layers.push({
        id: `weather_${Date.now()}`,
        name: 'Weather Effects',
        audioUrl: this.generateAudioUrl(`weather_${context.weather}`),
        volume: 0.3,
        loop: true,
        fadeInDuration: 2000,
        fadeOutDuration: 3000,
        isPlaying: false
      });
    }

    // Mood-based music layer
    layers.push({
      id: `music_${Date.now()}`,
      name: 'Mood Music',
      audioUrl: this.generateAudioUrl(`music_${context.mood}_${context.timeOfDay}`),
      volume: context.mood === 'action' ? 0.6 : 0.3,
      loop: true,
      fadeInDuration: 5000,
      fadeOutDuration: 4000,
      isPlaying: false
    });

    // Karma-influenced layer
    if (context.worldKarma < -20) {
      layers.push({
        id: `dark_${Date.now()}`,
        name: 'Dark Undertones',
        audioUrl: this.generateAudioUrl('dark_ambient_low_karma'),
        volume: 0.2,
        loop: true,
        fadeInDuration: 4000,
        fadeOutDuration: 3000,
        isPlaying: false
      });
    } else if (context.worldKarma > 20) {
      layers.push({
        id: `light_${Date.now()}`,
        name: 'Hopeful Tones',
        audioUrl: this.generateAudioUrl('hopeful_ambient_high_karma'),
        volume: 0.2,
        loop: true,
        fadeInDuration: 4000,
        fadeOutDuration: 3000,
        isPlaying: false
      });
    }

    return layers;
  }

  /**
   * Transition to a new soundscape profile
   */
  private async transitionToProfile(profile: SoundscapeProfile): Promise<void> {
    console.log('[Soundscape] Transitioning to profile:', profile.name);

    // Fade out current layers
    await this.fadeOutAllLayers();

    // Set new profile
    this.currentProfile = profile;

    // Fade in new layers
    await this.fadeInProfileLayers(profile);
  }

  private async fadeOutAllLayers(): Promise<void> {
    const fadePromises: Promise<void>[] = [];

    this.activeLayers.forEach((layer) => {
      if (layer.isPlaying && layer.audioElement) {
        fadePromises.push(this.fadeOutLayer(layer));
      }
    });

    await Promise.all(fadePromises);
    this.activeLayers.clear();
  }

  private async fadeInProfileLayers(profile: SoundscapeProfile): Promise<void> {
    const fadePromises: Promise<void>[] = [];

    profile.layers.forEach((layer) => {
      fadePromises.push(this.fadeInLayer(layer));
    });

    await Promise.all(fadePromises);
  }

  private async fadeInLayer(layer: SoundscapeLayer): Promise<void> {
    return new Promise((resolve) => {
      try {
        // Create audio element
        const audio = new Audio(layer.audioUrl);
        audio.loop = layer.loop;
        audio.volume = 0;

        layer.audioElement = audio;
        layer.isPlaying = true;

        // Add to active layers
        this.activeLayers.set(layer.id, layer);

        // Handle audio events
        audio.oncanplaythrough = () => {
          audio.play().then(() => {
            // Fade in
            this.fadeVolume(layer, 0, layer.volume * this.masterVolume, layer.fadeInDuration);
            resolve();
          }).catch((error) => {
            console.error('[Soundscape] Failed to play audio:', error);
            resolve();
          });
        };

        audio.onerror = (error) => {
          console.error('[Soundscape] Audio load error:', error);
          resolve();
        };

        // Load the audio
        audio.load();

      } catch (error) {
        console.error('[Soundscape] Failed to fade in layer:', error);
        resolve();
      }
    });
  }

  private async fadeOutLayer(layer: SoundscapeLayer): Promise<void> {
    return new Promise((resolve) => {
      if (!layer.audioElement || !layer.isPlaying) {
        resolve();
        return;
      }

      // Fade out
      this.fadeVolume(layer, layer.audioElement.volume, 0, layer.fadeOutDuration, () => {
        if (layer.audioElement) {
          layer.audioElement.pause();
          layer.audioElement = undefined;
        }
        layer.isPlaying = false;
        resolve();
      });
    });
  }

  private fadeVolume(
    layer: SoundscapeLayer,
    startVolume: number,
    endVolume: number,
    duration: number,
    onComplete?: () => void
  ): void {
    if (!layer.audioElement) return;

    const steps = 20;
    const stepDuration = duration / steps;
    const volumeStep = (endVolume - startVolume) / steps;
    let currentStep = 0;

    const fadeInterval = setInterval(() => {
      if (!layer.audioElement) {
        clearInterval(fadeInterval);
        return;
      }

      currentStep++;
      const newVolume = startVolume + (volumeStep * currentStep);
      layer.audioElement.volume = Math.max(0, Math.min(1, newVolume));

      if (currentStep >= steps) {
        clearInterval(fadeInterval);
        if (onComplete) onComplete();
      }
    }, stepDuration);

    // Store interval for cleanup
    this.fadeIntervals.set(layer.id, fadeInterval);
  }

  /**
   * Play a one-time sound effect
   */
  public async playSoundEffect(
    effectType: string,
    volume: number = 0.5,
    context?: any
  ): Promise<void> {
    if (!this.isEnabled) return;

    try {
      const audioUrl = this.generateAudioUrl(`effect_${effectType}`);
      const audio = new Audio(audioUrl);
      audio.volume = volume * this.masterVolume;

      await audio.play();
      console.log('[Soundscape] Played sound effect:', effectType);
    } catch (error) {
      console.error('[Soundscape] Failed to play sound effect:', error);
    }
  }

  /**
   * Update master volume
   */
  public setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    localStorage.setItem('lucidCyberRPG_masterVolume', this.masterVolume.toString());

    // Update all active layers
    this.activeLayers.forEach((layer) => {
      if (layer.audioElement && layer.isPlaying) {
        layer.audioElement.volume = layer.volume * this.masterVolume;
      }
    });

    console.log('[Soundscape] Master volume set to:', this.masterVolume);
  }

  /**
   * Enable/disable soundscape
   */
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    localStorage.setItem('lucidCyberRPG_soundEnabled', enabled.toString());

    if (!enabled) {
      this.fadeOutAllLayers();
    }

    console.log('[Soundscape] Soundscape enabled:', enabled);
  }

  /**
   * Get current soundscape status
   */
  public getStatus(): {
    isEnabled: boolean;
    masterVolume: number;
    currentProfile: string | null;
    activeLayers: number;
  } {
    return {
      isEnabled: this.isEnabled,
      masterVolume: this.masterVolume,
      currentProfile: this.currentProfile?.name || null,
      activeLayers: this.activeLayers.size
    };
  }

  /**
   * Emergency stop all audio
   */
  public emergencyStop(): void {
    console.log('[Soundscape] Emergency stop activated');

    // Clear all fade intervals
    this.fadeIntervals.forEach((interval) => {
      clearInterval(interval);
    });
    this.fadeIntervals.clear();

    // Stop all audio elements
    this.activeLayers.forEach((layer) => {
      if (layer.audioElement) {
        layer.audioElement.pause();
        layer.audioElement = undefined;
      }
      layer.isPlaying = false;
    });

    this.activeLayers.clear();
    this.currentProfile = null;
  }
}

// Export singleton instance
export const soundscapeService = new SoundscapeService();